<template>
    <v-row
        style="padding-top: 0px; padding-bottom: 0px; "
        v-if="other_employees_typing_text != null"
    >
        <v-col style="padding-top: 2px; padding-bottom: 2px;">
            <div style="display: flex; align-items: center; justify-content: flex-end;">
                <div class="message_text">
                    {{ other_employees_typing_text }}
                    <span class="first-dot"></span>
                    <span class="second-dot"></span>
                    <span class="third-dot"></span>
                </div>
            </div>
        </v-col>

        <v-col class="right_letter_gutter text-center" align-self="end">
        </v-col>
    </v-row>

</template>

<script>

/**
 * Controls the frequency that someone typing will send isTyping signals.
 * @type {number}
 */
const IS_TYPING_UPDATE_INTERVAL = 1000;

/**
 * Interval to keep the "is typing" text around for before removing it (unless the interval is refreshed)
 * @type {number}
 */
const CLEAR_IS_TYPING_TEXT_INTERVAL = 3000;

/**
 * This is a master switch to turn off the is_typing mechanism. Use it to quickly turn off the system (requires a front end deploy)
 * @type {boolean}
 */
const HARD_SHUT_OFF_FUNCTIONALITY = false;

export default {
    props: {
        conversation_id: {
            type: String,
            default: function() { return null; },
        },
    },

    data() {
        return {
            can_post_update: true,
            other_employees_typing: [],
            other_employees_typing_text: null,
        }
    },

    created() {
        if ( HARD_SHUT_OFF_FUNCTIONALITY ) return;

        this.$event_bus.$off('endpoint-update-is-typing');
        this.$event_bus.$on('endpoint-update-is-typing', this.onUpdateConversationIsTyping);
    },

    beforeDestroy() {
        this.$event_bus.$off('endpoint-update-is-typing', this.onUpdateConversationIsTyping);
    },

    methods: {

        onUpdateConversationIsTyping(signal)
        {
            if ( HARD_SHUT_OFF_FUNCTIONALITY ) return;

            if ( this.$rc.$state.employee_id == signal.employee_id ) return;
            
            if ( this.conversation_id != signal.conversation_id ) return;

            let employee_id = signal.employee_id;

            if ( !signal.is_typing )
            {
                this.deleteEmployeeIsTyping(employee_id);
                return;
            }

            this.clearOtherEmployeeTimeout(employee_id);

            let clear_ref = this.deleteEmployeeIsTyping;

            let clear_timer_id = setTimeout(function() {
                clear_ref(employee_id);
            }, CLEAR_IS_TYPING_TEXT_INTERVAL);

            let employee_data = {
                'employee_id' : employee_id,
                'employee_label' : signal.employee_label,
                'clear_timer_id' : clear_timer_id,
            };

            this.other_employees_typing[employee_id] = employee_data;
            this.resetOtherEmployeesAreTypingText();
        },

        resetOtherEmployeesAreTypingText() {
            if ( Object.keys(this.other_employees_typing).length == 0 )
            {
                this.other_employees_typing_text = null;
                return;
            }

            if ( Object.keys(this.other_employees_typing).length == 1 )
            {
                let first_element = this.other_employees_typing[Object.keys(this.other_employees_typing)[0]];
                this.other_employees_typing_text = first_element.employee_label + ' is typing';
                return;
            }

            // multiple people are typing, separate them with a comma in a nice way
            let labels = [];

            for (const [key, value] of Object.entries(this.other_employees_typing))
            {
                labels.push(value.employee_label);
            }

            let labels_concat = labels.join(', ');

            this.other_employees_typing_text = labels_concat + ' are typing';
        },

        clearOtherEmployeeTimeout(employee_id)
        {
            if ( !this.other_employees_typing[employee_id] ) return;

            let clear_timer_id = this.other_employees_typing[employee_id].clear_timer_id;

            clearTimeout(clear_timer_id);
        },

        deleteEmployeeIsTyping(employee_id)
        {
            delete this.other_employees_typing[employee_id];
            this.resetOtherEmployeesAreTypingText();
        },

        updateIsTyping(for_endpoint, conversation_id, employee_id, is_typing_value=true)
        {
            if ( HARD_SHUT_OFF_FUNCTIONALITY ) return;

            if ( !this.can_post_update ) return;

            this.$rc.setConversationIsTyping(
                for_endpoint,
                conversation_id,
                employee_id,
                is_typing_value,
            );

            this.can_post_update = false;
            let post_update_ref = this.resetPostUpdateStatus;

            // throttle the updates to redchirp to prevent throttling
            setTimeout(function() {
                post_update_ref();
            }, IS_TYPING_UPDATE_INTERVAL);
        },

        resetPostUpdateStatus()
        {
            this.can_post_update = true;
        },
    },
}
</script>

<style scoped>

.right_letter_gutter
{
    max-width: 60px;
    min-width: 60px;
    padding-top: 2px;
    padding-bottom: 5px;
    padding-left: 0px;
}

.message_text
{
    background-color: rgb(241,243,244);
    color: rgb(32,33,36);
    display: inline-block;
    border-radius: 20px;
    font-family: Roboto;

    padding-bottom: 10px;
    padding-left: 14px;
    padding-right: 14px;
    padding-top: 10px;

    line-height: 24px;

    max-width: 460px;

    overflow-wrap: anywhere;

    margin-bottom: 15px;
}

.is_typing
{
    position: fixed;
    bottom: 30px;
    height: 60px;
    left: 380px;
    right: 40px;

    font-family: Roboto;
    font-style: italic;
    font-size: 14px;

    z-index: 1;
}

.first-dot {
    display:inline-block;
    width:5px;
    height:5px;
    border-radius:50%;
    margin-right:3px;
    background:#303131;
    animation: wave 1.3s linear infinite;
}

.second-dot {
    display:inline-block;
    width:5px;
    height:5px;
    border-radius:50%;
    margin-right:3px;
    background:#303131;
    animation: wave 1.3s linear infinite;
    animation-delay: -1.1s;
}

.third-dot {
    display:inline-block;
    width:5px;
    height:5px;
    border-radius:50%;
    margin-right:3px;
    background:#303131;
    animation: wave 1.3s linear infinite;
    animation-delay: -0.9s;
}

@keyframes wave {
    0%, 60%, 100% {
        transform: initial;
    }
    30% {
        transform: translateY(-10px);
    }
}
</style>